import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/Marketing";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Home, LayoutTemplate, Compass, BarChart3, Plus, LogOut, User, Layers, Building2, GitCompare, ShieldCheck, Bell } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Home", icon: Home },
  { to: "/templates", label: "Templates", icon: LayoutTemplate },
  { to: "/collections", label: "Collections", icon: Layers },
  { to: "/workspace", label: "Workspace", icon: Building2 },
  { to: "/compare", label: "Compare", icon: GitCompare },
  { to: "/discover", label: "Discover", icon: Compass },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
];

export default function DashboardShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const initials = (user?.name || "U").slice(0, 1).toUpperCase();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get("/admin/pending-count"); if (alive) setPending(data.count || 0); } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user?.role, loc.pathname]);

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-border p-5 sticky top-0 h-screen">
        <Logo />
        <nav className="mt-8 space-y-1 flex-1">
          {[...NAV, ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldCheck }] : [])].map(({ to, label, icon: Icon }) => {
            const active = loc.pathname === to;
            const showBadge = to === "/admin" && pending > 0;
            return (
              <Link key={to} to={to} data-testid={`side-${label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active ? "bg-accent text-primary" : "text-foreground/70 hover:bg-muted hover:text-foreground"}`}>
                <Icon size={18} /> <span className="flex-1">{label}</span>
                {showBadge && <span data-testid="admin-pending-badge" className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">{pending}</span>}
              </Link>
            );
          })}
        </nav>
        <Link to="/create"><Button className="w-full rounded-full" data-testid="sidebar-create-btn"><Plus size={16} className="mr-1" /> Create Timeline</Button></Link>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 glass h-16 flex items-center justify-between px-5 sm:px-8">
          <div className="md:hidden"><Logo /></div>
          <div className="hidden md:block text-sm text-muted-foreground capitalize">{user?.subscription} plan</div>
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <Link to="/admin" className="relative" data-testid="admin-bell" title={pending > 0 ? `${pending} pending payment${pending > 1 ? "s" : ""}` : "No pending payments"}>
                <Button variant="ghost" size="icon" className="rounded-full"><Bell size={18} /></Button>
                {pending > 0 && <span data-testid="admin-bell-count" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 grid place-items-center text-[10px] font-bold rounded-full bg-primary text-primary-foreground ring-2 ring-background">{pending}</span>}
              </Link>
            )}
            <ThemeToggle />
            <Link to="/create" className="md:hidden"><Button size="sm" className="rounded-full"><Plus size={16} /></Button></Link>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu">
                <Avatar className="h-9 w-9 border border-border cursor-pointer"><AvatarFallback className="bg-primary text-primary-foreground text-sm">{initials}</AvatarFallback></Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to={`/u/${user?.username}`}><User size={15} className="mr-2" /> Profile</Link></DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn"><LogOut size={15} className="mr-2" /> Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 px-5 sm:px-8 py-8">{children}</main>
      </div>
    </div>
  );
}
